import { useUser } from '@/services/user';
import { useLocalStorage } from '@vueuse/core';
import { computed, onMounted, ref } from 'vue';

export const usePush = () => {
  const user = useUser();

  const requestPermission = async () => {
    const permission = await Notification.requestPermission();

    return permission;
  };

  const register = () => {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    try {
      return navigator.serviceWorker.register('/push.js');
    } catch (e) {
      return false;
    }
  };

  const isSubscribed = ref(false);
  const isSupported = ref(false);

  const supported = () => {
    if (!('serviceWorker' in navigator)) {
      return false;
    }

    if (!('Notification' in window)) {
      return false;
    }

    if (!('PushManager' in window)) {
      return false;
    }

    return true;
  };

  const checkPushSubscription = async () => {
    if (!supported()) {
      isSubscribed.value = false;

      return;
    }

    const sw = await navigator.serviceWorker.ready;

    if (!sw) {
      throw new Error('Service worker failed to ready on subscribe.');
    }

    const subscription = await sw.pushManager.getSubscription();

    if (!subscription) {
      isSubscribed.value = false;

      return;
    }

    isSubscribed.value = true;
  };

  isSupported.value = supported();

  onMounted(checkPushSubscription);

  const loading = ref(false);

  const subscribe = async (): Promise<'subscribed' | 'aborted' | 'failed'> => {
    if (loading.value === true || isSupported.value === false) {
      return 'aborted';
    }

    loading.value = true;

    const permission = await requestPermission();

    if (permission !== 'granted') {
      loading.value = false;

      return 'aborted';
    }

    const sw = await navigator.serviceWorker.ready;

    if (!sw) {
      throw new Error('Service worker failed to ready on subscribe.');
    }

    let subscription = await sw.pushManager.getSubscription();

    if (!subscription) {
      subscription = await sw.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: import.meta.env.VITE_PUSH_PUBLIC_KEY,
      });
    }

    try {
      await user.push.subscription.update(subscription.toJSON());
    } catch (error) {
      console.error(error);

      loading.value = false;

      return 'failed';
    }

    await checkPushSubscription();

    loading.value = false;

    return 'subscribed';
  };

  /**
   * 30 days in milliseconds
   */
  const period = 30 * 24 * 60 * 60 * 1000;
  const lastPromptedAt = useLocalStorage<number | undefined>('push.promptedAt', undefined);

  const isDismissalPeriodElapsed = computed(() => {
    if (lastPromptedAt.value === undefined) {
      return true;
    }

    return Date.now() - lastPromptedAt.value > period;
  });

  const isPromptable = computed(() => {
    return isSupported.value && !isSubscribed.value && isDismissalPeriodElapsed.value;
  });

  const dismiss = () => {
    lastPromptedAt.value = Date.now();
  };

  return {
    register,
    subscribe,
    supported,
    dismiss,
    isSupported,
    isSubscribed,
    isPromptable,
    loading,
  };
};
